import { Listbox } from "@headlessui/react";
import { Fragment, Key, ReactNode, useMemo } from "react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import classNames from "classnames";
import { Float } from "@headlessui-float/react";

export interface ListBoxProps<T> {
  className?: string;
  value?: T;
  valueChanged?: (value: T | undefined) => void;
  values: T[];
  valueTemplate?: JSX.Element;
  itemTemplate?: JSX.Element;
  itemDisabled: (item: T) => boolean;
  valueIdentifier?: (item: T) => string | number;
  valueKey?: (item: T) => string | number;
  placeholder?: string;
  name: string;
  showClearValue?: boolean;
}

function ListBox<T>({
  value,
  valueChanged,
  valueIdentifier,
  values,
  className,
  valueKey,
  placeholder,
  itemDisabled = (_) => false,
  name,
  showClearValue = false,
}: ListBoxProps<T>) {
  const selectedValue = useMemo(() => {
    // Workaround to ensure the same ref is used as value and as item in the values array
    const identifier =
      valueIdentifier && value ? valueIdentifier(value) : value;
    const valueRef = values.find(
      (i) => (valueIdentifier ? valueIdentifier(i) : i) === identifier,
    );
    return valueRef;
  }, [value, valueIdentifier, values]);

  const clearValue = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (valueChanged) {
      valueChanged(undefined);
    }
  };

  return (
    <Listbox
      as="div"
      className={classNames("", className)}
      value={selectedValue}
      onChange={valueChanged}
    >
      <Float
        as="div"
        className="relative w-full"
        placement="bottom-start"
        flip={0}
        offset={4}
        transform
        adaptiveWidth
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Listbox.Button
          className="relative min-h-[42px] w-full rounded-lg border border-solid border-gray-100 bg-white px-4 py-2"
          data-cy={`listbox-${name}`}
        >
          {selectedValue && (
            <span className="mr-4 block truncate text-start text-gray-900">
              {
                (valueKey
                  ? valueKey(selectedValue)
                  : selectedValue) as ReactNode
              }
            </span>
          )}
          {!selectedValue && (
            <span className="mr-4 block truncate text-start text-gray-500">
              {placeholder}
            </span>
          )}
          {(!showClearValue || !selectedValue) && (
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          )}
          {showClearValue && !!selectedValue && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              onClick={clearValue}
            >
              <XMarkIcon className="h-5 w-5 text-th-primary-900" />
            </button>
          )}
        </Listbox.Button>
        <Listbox.Options className="relative z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {values.map((option) => (
            <Listbox.Option
              key={(valueIdentifier ? valueIdentifier(option) : option) as Key}
              value={option}
              disabled={itemDisabled(option)}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 text-gray-900 ${
                  active ? "bg-th-primary-100" : ""
                }`
              }
              data-cy={
                (valueIdentifier ? valueIdentifier(option) : option) as Key
              }
            >
              <Fragment>
                <span
                  className={`block truncate ${
                    option === selectedValue ? "font-medium" : "font-normal"
                  } ${itemDisabled(option) ? "text-gray-500" : ""}`}
                >
                  {(valueKey ? valueKey(option) : option) as ReactNode}
                </span>
                {option === selectedValue ? (
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-th-primary-900">
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                ) : null}
              </Fragment>
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Float>
    </Listbox>
  );
}

export default ListBox;
